import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConditionalAccessPolicy } from '@microsoft/microsoft-graph-types-beta';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import {
    BehaviorSubject,
    Subscription,
    combineLatest,
    debounceTime,
    first,
    map,
    skipUntil,
    switchMap,
    tap,
} from 'rxjs';
import { CapMappingDialogComponent, CapMappingDialogData } from '../cap-mapping-dialog/cap-mapping-dialog.component';
import { NoCapSchema } from '../cap-schema';
import { genericCheckIsBlockingEveryone } from '../utlize';
import {
    checkAnyConditionWithSeiCustomizeContinuousAccessDisabled,
    checkIsNoIncludeUsers,
    checkIsNoSessionOrGrantControl,
    checkIsPersistentBrowserSessionWithNoResource,
} from '../cap-generic-form';
import { TemplateItem } from 'projects/angular-clarity/src/app/modules/sway/store/templates/items/model';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import {
    createConditionalAccessPolicySuccess,
    deleteConditionalAccessPolicySuccess,
} from 'projects/angular-clarity/src/app/stores/client/graph/conditional-access/actions';
import { updateBaseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/actions';
import { Baseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/model';
import { selectSession } from 'projects/angular-clarity/src/app/stores/root.store';

@Component({
    selector: 'sway-cap-generic-baseline',
    templateUrl: './cap-generic-baseline.component.html',
    styleUrls: ['./cap-generic-baseline.component.scss', '../../../../styles/baseline-style.scss'],
})
export class CapGenericBaselineComponent implements OnInit, OnDestroy {
    @Input() form: FormGroup;
    @Input() tenant_id: string;
    @Input() readOnly: boolean;
    @Input() baselineId: string;
    @Input() baselineTemplateItem: TemplateItem;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    @Input() schemaFunc: Function;
    @Input() errorsString = '';
    @Output() baselineChange = new EventEmitter<Baseline>();
    @Output() baselineErrorChange = new EventEmitter<{ remediate: boolean; save: boolean }>();
    isBlockingEveryOne: boolean = false;
    allConditionalAccessPolicies: ConditionalAccessPolicy[] = [];
    isSecurityDefaultsEnabled: boolean;

    // TODO, karim git rid of this
    @Input() policy$: BehaviorSubject<ConditionalAccessPolicy>;
    @Input() schema$: BehaviorSubject<any>;
    noIncludeUsers: boolean;
    baseline: Baseline;
    isNoSessionOrGrantControl: boolean;
    conditionWithSeiCustomizeContinuousAccessDisabled: boolean;
    isPersistentBrowserSessionWithNoResource: boolean;

    constructor(
        private store: Store<any>,
        private action: Actions,
        private dialog: MatDialog,
    ) {}

    subs: Subscription[] = [];

    securityDefaults$ = this.store.select(selectSession).pipe(
        map((sess) => sess.session.clientId),

        switchMap((tid) =>
            combineLatest([
                this.store.select(client(tid).graph.securityDefaults.item),
                this.store.select(client(tid).graph.securityDefaults.status),
            ]),
        ),
        first(([_, status]) => status.loaded),
        map(([data]) => data),
    );

    ngOnInit(): void {
        this.checkStatus();

        this.observeCAPPoliciesCreationOrDeletion();

        const loaded$ = this.store.pipe(
            select(client(this.tenant_id).graph.conditionalAccessPolicy.status),
            map((status) => status.loaded),
        );

        const baseline$ = this.store.pipe(select(client(this.tenant_id).sway.baselines.byId(this.baselineId)));

        const policies$ = this.store.pipe(
            select(client(this.tenant_id).graph.conditionalAccessPolicy.all),
            skipUntil(loaded$),
            tap((allPolicies) => {
                this.allConditionalAccessPolicies = allPolicies;
            }),
        );

        const sub = combineLatest([baseline$, policies$, this.securityDefaults$])
            .pipe(
                map(([baseline, policies, securityDefaults]) => {
                    const schema = JSON.parse(JSON.stringify(baseline.schema));

                    if (policies instanceof Array) {
                        if (baseline.key) {
                            const id = baseline.key;
                            const policy = policies.find((res) => res.id === id);

                            if (!policy && !!id) {
                                const data: Baseline = { ...baseline, key: null };

                                const _tenant = this.tenant_id;

                                this.store.dispatch(updateBaseline({ _tenant, data }));
                            }

                            return { policy: policy || {}, baseline, securityDefaults };
                        } else {
                            const displayName =
                                schema?.contains?.properties?.displayName?.const ||
                                schema?.not?.contains?.properties?.displayName?.const;

                            const policy = policies.find((res) => res.displayName === displayName) || {};

                            if (policy.id && !!schema.contains) {
                                // there is a policy, and it has not deleted baseline

                                const data: Baseline = { ...baseline, schema: schema, key: policy.id };

                                const _tenant = this.tenant_id;

                                this.store.dispatch(updateBaseline({ _tenant, data }));
                            }

                            return { policy, baseline, securityDefaults };
                        }
                    }
                }),
            )
            .subscribe(({ policy, baseline, securityDefaults }) => {
                this.baseline = baseline;
                this.policy$.next(policy);
                this.isSecurityDefaultsEnabled = securityDefaults.isEnabled;

                if (this.isSecurityDefaultsEnabled) {
                    this.baselineErrorChange.next({
                        remediate: true,
                        save: false,
                    });
                }
            });

        const valueChanges$ = this.form.valueChanges.pipe(debounceTime(100));

        const sub2 = valueChanges$.subscribe((res) => {
            const formValues = this.form.getRawValue();
            let schema;

            if (formValues.state === 'delete') {
                schema = JSON.parse(JSON.stringify(NoCapSchema(formValues.displayName)));
                this.baselineErrorChange.next({
                    remediate: false,
                    save: false,
                });
                this.schema$.next(schema);
                this.isBlockingEveryOne = null;
                this.noIncludeUsers = null;
                this.isPersistentBrowserSessionWithNoResource = null;
                this.isNoSessionOrGrantControl = null;
                this.conditionWithSeiCustomizeContinuousAccessDisabled = null;
                this.baselineChange.next({
                    ...this.baseline,
                    schema: JSON.parse(JSON.stringify(schema)),
                });

                return;
            }

            schema = this.setSchemaValue();

            this.schema$.next(schema);

            this.checkStatus();

            if (this.isSecurityDefaultsEnabled) {
                this.baselineErrorChange.next({
                    remediate: true,
                    save: false,
                });
            }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(schema)),
            });
        });

        this.subs.push(sub, sub2);
    }

    checkStatus() {
        if (this.form.get('displayName').errors?.required) {
            this.baselineErrorChange.next({
                remediate: true,
                save: true,
            });
            return;
        }

        // clear any old values
        this.baselineErrorChange.next({
            remediate: false,
            save: false,
        });

        const formValues = this.form.getRawValue();

        this.isBlockingEveryOne = genericCheckIsBlockingEveryone(formValues);
        this.noIncludeUsers = checkIsNoIncludeUsers(formValues);
        this.isNoSessionOrGrantControl = checkIsNoSessionOrGrantControl(formValues);
        this.conditionWithSeiCustomizeContinuousAccessDisabled =
            checkAnyConditionWithSeiCustomizeContinuousAccessDisabled(formValues);

        this.isPersistentBrowserSessionWithNoResource = checkIsPersistentBrowserSessionWithNoResource(formValues);

        if (
            this.noIncludeUsers ||
            this.isBlockingEveryOne ||
            this.isNoSessionOrGrantControl ||
            this.conditionWithSeiCustomizeContinuousAccessDisabled ||
            this.isPersistentBrowserSessionWithNoResource
        ) {
            this.baselineErrorChange.next({
                remediate: true,
                save: true,
            });
        } else {
            this.baselineErrorChange.next({
                remediate: false,
                save: false,
            });
        }
    }

    setSchemaValue(): any {
        const formValues = this.form.getRawValue();

        const schema = JSON.parse(JSON.stringify(this.schemaFunc(formValues)));

        return schema;
    }

    mapPolicy(id: string) {
        const data: Baseline = { ...this.baseline, key: id };

        const _tenant = this.tenant_id;

        const schema = JSON.parse(JSON.stringify(this.schemaFunc(this.form.getRawValue())));

        console.log(schema, 'schema');

        data.schema = schema;

        this.store.dispatch(updateBaseline({ _tenant, data }));
    }

    observeCAPPoliciesCreationOrDeletion() {
        const sub = this.action.pipe(ofType(createConditionalAccessPolicySuccess)).subscribe((res) => {
            if (res) {
                this.mapPolicy(res.policy.id);
            }
        });

        const sub2 = this.action.pipe(ofType(deleteConditionalAccessPolicySuccess)).subscribe((res) => {
            if (res) {
                const data: Baseline = { ...this.baseline, key: null };

                const _tenant = this.tenant_id;

                this.store.dispatch(updateBaseline({ _tenant, data }));
            }
        });

        this.subs.push(sub, sub2);
    }

    openRemapPolicyDialog() {
        const data: CapMappingDialogData = {
            baselineId: this.baseline.id,
            selectedPolicyId: this.baseline.key,
            allPolicies: this.allConditionalAccessPolicies,
            tenantId: this.tenant_id,
        };

        const ref = this.dialog.open(CapMappingDialogComponent, {
            data,
        });

        ref.afterClosed().subscribe((res) => {
            if (res?.success) {
                this.mapPolicy(res.data.id);
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });
    }
}
